import { stringify } from "yaml";

export type DiffSegment = {
	type: "removed" | "added" | "unchanged" | "changed";
	left?: string;
	right?: string;
};

/**
 * Simple line-based diff of two objects (serialized to YAML).
 * Returns segments: removed (left only, red), added (right only, green), unchanged (both), changed (both sides different, left red right orange).
 */
export function objectDiffToSegments(leftObj: unknown, rightObj: unknown): DiffSegment[] {
	const leftStr = typeof leftObj === "object" && leftObj !== null ? stringify(leftObj, { indent: 2 }).trim() : "";
	const rightStr = typeof rightObj === "object" && rightObj !== null ? stringify(rightObj, { indent: 2 }).trim() : "";
	const leftLines = leftStr ? leftStr.split(/\r?\n/) : [];
	const rightLines = rightStr ? rightStr.split(/\r?\n/) : [];

	const segments: DiffSegment[] = [];
	const n = leftLines.length;
	const m = rightLines.length;

	// LCS table for alignment
	const dp: number[][] = Array(n + 1).fill(null).map(() => Array(m + 1).fill(0));
	for (let i = 1; i <= n; i++) {
		for (let j = 1; j <= m; j++) {
			if (leftLines[i - 1] === rightLines[j - 1]) {
				dp[i][j] = dp[i - 1][j - 1] + 1;
			} else {
				dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
			}
		}
	}

	// Backtrack to get alignment
	let i = n;
	let j = m;
	const aligned: DiffSegment[] = [];
	while (i > 0 || j > 0) {
		if (i > 0 && j > 0 && leftLines[i - 1] === rightLines[j - 1]) {
			aligned.push({ type: "unchanged", left: leftLines[i - 1], right: rightLines[j - 1] });
			i--;
			j--;
		} else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
			aligned.push({ type: "added", right: rightLines[j - 1] });
			j--;
		} else if (i > 0) {
			aligned.push({ type: "removed", left: leftLines[i - 1] });
			i--;
		}
	}
	return aligned.reverse();
}
